import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'antd';
import { Printer } from 'lucide-react';
import XiaoMaLogoSvg from 'components/xiao-ma-logo-svg';
import { SHOP_MAP } from 'constant/shop';
import {
  ReceptionProfession,
  ReceptionSnsPlatform,
  ReceptionVisitMotive,
} from 'types/oms';
import {
  RECEPTION_COPY,
  resolveReceptionLang,
} from 'pages/rrs/recycling-consignment-detail/reception-form/locales';

const PRINT_PAGE_STYLE = `
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }
  html, body, #root {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
}
`;

const Field = ({ label, value }: { label: string; value?: string }) => {
  return (
    <div>
      <div className="mb-1 text-sm text-neutral-600">{label}</div>
      <div className="whitespace-pre-wrap break-words text-base leading-6">
        {value || '\u00a0'}
      </div>
    </div>
  );
};

const ReceptionPrint = () => {
  const [searchParams] = useSearchParams();
  const lang = resolveReceptionLang(searchParams.get('lang'));
  const copy = RECEPTION_COPY[lang];
  const storeId = Number(searchParams.get('storeId')) || undefined;
  const submissionId = searchParams.get('submissionId') || '';
  const profession = searchParams.get('profession');
  const visitMotive = searchParams.get('visitMotive');
  const snsPlatform = searchParams.get('snsPlatform');
  const professionOther = searchParams.get('professionOther') || '';
  const visitMotiveOther = searchParams.get('visitMotiveOther') || '';

  const professionLabel =
    profession && profession in copy.professionOptions
      ? copy.professionOptions[profession as ReceptionProfession]
      : '';
  const visitMotiveLabel =
    visitMotive && visitMotive in copy.visitMotiveOptions
      ? copy.visitMotiveOptions[visitMotive as ReceptionVisitMotive]
      : '';
  const snsLabel =
    snsPlatform && snsPlatform in copy.snsOptions
      ? copy.snsOptions[snsPlatform as ReceptionSnsPlatform]
      : '';
  const professionText =
    profession === 'other' && professionOther
      ? `${professionLabel}（${professionOther}）`
      : professionLabel;
  const visitMotiveText =
    visitMotive === 'other' && visitMotiveOther
      ? `${visitMotiveLabel}（${visitMotiveOther}）`
      : visitMotiveLabel;

  useEffect(() => {
    const style = document.createElement('style');
    style.dataset.receptionPrint = 'true';
    style.textContent = PRINT_PAGE_STYLE;
    document.head.appendChild(style);
    const closePage = () => {
      window.close();
    };
    window.addEventListener('afterprint', closePage);
    const timer = window.setTimeout(() => {
      window.print();
    }, 300);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', closePage);
      style.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-neutral-200 py-6 print:bg-white print:py-0">
      <div
        className="mx-auto box-border bg-white text-neutral-900 shadow-sm print:shadow-none"
        style={{
          width: '210mm',
          height: '297mm',
          padding: '16mm 18mm',
          overflow: 'hidden',
        }}
      >
        <header className="text-center">
          <XiaoMaLogoSvg className="mx-auto h-14 w-auto" />
          {storeId === SHOP_MAP.UMEDA && (
            <div className="mt-1 text-xs tracking-[0.45em]">UMEDA</div>
          )}
          <div className="mt-5 text-xl font-medium tracking-wide">
            {copy.formTitle}
          </div>
          <div className="mt-0.5 text-sm">{copy.formSubtitle}</div>
          <div className="mt-4 flex items-end justify-between gap-6 border-b border-neutral-900 pb-1.5 text-sm">
            <div className="text-left leading-tight">
              {submissionId && (
                <>
                  <div>{copy.submissionId}</div>
                  <div>{submissionId}</div>
                </>
              )}
            </div>
            <div className="flex items-end gap-6">
              <div className="text-left leading-tight">
                <div>{copy.dateLabel}</div>
                {copy.dateSubLabel && (
                  <div className="text-xs">{copy.dateSubLabel}</div>
                )}
              </div>
              <div className="flex items-end gap-2">
                <span className="min-w-12 text-center">
                  {searchParams.get('year') || ''}
                </span>
                <span>{copy.year}</span>
                <span className="min-w-6 text-center">
                  {searchParams.get('month') || ''}
                </span>
                <span>{copy.month}</span>
                <span className="min-w-6 text-center">
                  {searchParams.get('day') || ''}
                </span>
                {copy.day && <span>{copy.day}</span>}
              </div>
            </div>
          </div>
        </header>

        <div className="mt-6 space-y-3">
          <Field
            label={copy.furigana}
            value={searchParams.get('furigana') || ''}
          />
          <Field label={copy.name} value={searchParams.get('name') || ''} />
          <Field
            label={copy.postCode}
            value={
              searchParams.get('postCode')
                ? `〒${searchParams.get('postCode')}`
                : ''
            }
          />
          <Field
            label={copy.address}
            value={searchParams.get('address') || ''}
          />
          <Field label={copy.tel} value={searchParams.get('tel') || ''} />
          <Field label={copy.email} value={searchParams.get('email') || ''} />
          {professionText && (
            <Field label={copy.profession} value={professionText} />
          )}
          <div className="text-xs leading-5 text-neutral-600">
            {copy.privacy}
          </div>
          {visitMotiveText && (
            <Field label={copy.visitMotive} value={visitMotiveText} />
          )}
          {visitMotive === 'sns' && snsLabel && (
            <Field label={copy.sns} value={snsLabel} />
          )}
        </div>
      </div>

      <div className="fixed bottom-6 left-1/2 z-10 -translate-x-1/2 print:hidden">
        <Button
          className="h-12 px-8 text-base"
          icon={<Printer className="h-4 w-4" />}
          style={{
            backgroundColor: '#171717',
            borderColor: '#171717',
            color: '#fff',
          }}
          onClick={() => window.print()}
        >
          {copy.print}
        </Button>
      </div>
    </div>
  );
};

export default ReceptionPrint;
